import config from "../../shared/config/config";
import {
  getUserInfoFromLocalStorage,
  getWorkspaceCode,
} from "../../shared/utils/auth";
import axios from "../../shared/utils/axios";
import axiosAdmin from "../../shared/utils/axiosAdmin";
import axiosNoLoading from "../../shared/utils/axiosNoLoading";
import { EcoCommonConfig, LoginDto } from "../../types/auth";
import { BasicReturnMsg } from "../../types/common";
import { ZoomRespondDto } from "../../types/meeting";
import {
  CalendarCount,
  CalendarDetail,
  FavoriteItemListReqDto,
  FavoriteItemListRespDto,
  Member,
  MemberSearchDto,
  MyTaskDashboard,
  NotificationDTO,
  Policy,
  TreeDataRequest,
  TreeDataResponse,
  UserSimpleInfo,
  Workgroup,
  WorkgroupDetailDTO,
  WorkgroupHomeDashboard,
  WorkgroupRespDto,
  WorkgroupSearchDto,
  WorkgroupWithPermissionPolicyTarget,
} from "../../types/workgroup";

const GATEWAY_URL = config.servers.gateway.url;

interface WorkgroupApi {
  getWorkgroupSimpleList(req: WorkgroupSearchDto): Promise<WorkgroupRespDto>;
  getWorkgroupSideList(reqDTO: WorkgroupSearchDto): Promise<WorkgroupRespDto>;
  getMemberList(req: MemberSearchDto): Promise<MemberSearchDto>;
  getWorkgroupFullList(req: WorkgroupSearchDto): Promise<WorkgroupRespDto>;
  getTotalFavoriteItems(
    req: FavoriteItemListReqDto
  ): Promise<FavoriteItemListRespDto>;
  getWorkgroupDetail(req: { workgroupId: string }): Promise<WorkgroupDetailDTO>;
  doWorkgroupFavorite(
    workgroupId: string,
    users: {
      userId: string;
      favorite: string;
    }[]
  ): Promise<Workgroup[]>;
  membersProfile(userId: string): Promise<Member>;
  getOrgansTree(treeDataRequest: TreeDataRequest): Promise<TreeDataResponse>;
  workgroupChangeInfo(users: UserSimpleInfo[]): Promise<BasicReturnMsg>;
  getFavoriteMeetingList(req: FavoriteItemListReqDto): Promise<ZoomRespondDto>;
  getWorkspaceConfig(): Promise<EcoCommonConfig>;
  createNewWorkgroup(
    workgroupRequest: WorkgroupWithPermissionPolicyTarget
  ): Promise<Workgroup>;
  createNewPrivateWorkgroups(
    workgroupRequest: WorkgroupWithPermissionPolicyTarget
  ): Promise<Workgroup>;
  updateWorkgroupInfo(req: {
    workgroupId: string;
    managerId?: string;
    workgroup?: Workgroup;
    policy?: Policy;
  }): Promise<BasicReturnMsg>;
  resendInviteMail(workgroupId: string, userId: string): Promise<any>;
  getWorkgroupNoti(workgroupId: string): Promise<NotificationDTO>;
  updateWorkgroupNoti(
    workgroupId: string,
    req: NotificationDTO
  ): Promise<NotificationDTO>;
  workgroupUpdateStatus(
    workgroupId: string,
    status: number
  ): Promise<Workgroup[]>;
  workgroupmemberWithdraw(
    workgroupId: string,
    users: {
      userId: string;
      userType: number;
      withdrawYN: string;
    }[]
  ): Promise<Member>;
  workgroupHomeDashboard(workgroupId: string): Promise<WorkgroupHomeDashboard>;
  getMyDashboardTasks(): Promise<MyTaskDashboard>;
  getMyDashboardSuggestion(): Promise<MyTaskDashboard>;
  getWorkgroupCalendarCount(req: {
    workgroupId: string;
    date: number;
    startDate?: number;
    endDate?: number;
    offset: number;
  }): Promise<CalendarCount[]>;
  getWorkgroupCalendarDetail(req: {
    workgroupId: string;
    date: number;
  }): Promise<CalendarDetail>;
  getRecentHistoryWorkgroupList(req: {
    startDate: number;
    endDate: number;
  }): Promise<Workgroup[]>;
  deleteMember(userId: string): Promise<BasicReturnMsg>;
  checkCheckoutUser(req: {
    userIds: string[];
    workgroupId?: string;
  }): Promise<BasicReturnMsg>;
  getNesetedWorkgroupList(): Promise<Workgroup[]>;
  moveWorkgroup(req: {
    workgroupId: string;
    targetParentWorkgroupId: string;
  }): Promise<Workgroup>;
}

class WorkgroupServerApi implements WorkgroupApi {
  async getWorkgroupSimpleList(
    req: WorkgroupSearchDto
  ): Promise<WorkgroupRespDto> {
    const resp = await axios.post<WorkgroupRespDto>(
      `${GATEWAY_URL}/wapi/workgroups/simpleList`,
      req
    );
    return resp.data;
  }
  async getWorkgroupSideList(
    reqDTO: WorkgroupSearchDto
  ): Promise<WorkgroupRespDto> {
    const resp = await axios.post<WorkgroupRespDto>(
      `${GATEWAY_URL}/wapi/workgroups/sideList`,
      reqDTO
    );
    return Promise.resolve(resp.data);
  }
  async getOrgansTree(
    treeDataRequest: TreeDataRequest
  ): Promise<TreeDataResponse> {
    const resp =
      window.eco.serviceType === "OCI"
        ? await axios.post<TreeDataResponse>(
            `${GATEWAY_URL}/wapi/tenancy/member/list`,
            {
              dsdCode: getWorkspaceCode(),
              userType: 1,
            }
          )
        : await axios.post<TreeDataResponse>(
            `${GATEWAY_URL}/wapi/organs/tree`,
            treeDataRequest
          );
    if (window.eco.serviceType !== "OCI") resp.data.path = treeDataRequest.path;

    return Promise.resolve(resp.data);
  }
  async getMemberList(req: MemberSearchDto): Promise<MemberSearchDto> {
    const resp =
      window.eco.serviceType === "OCI"
        ? await axios.post<MemberSearchDto>(
            `${GATEWAY_URL}/wapi/tenancy/member/list`,
            {
              dsdCode: getWorkspaceCode(),
              searchOpt: req.searchOpt,
              userType:
                req.type === "external"
                  ? 2
                  : req.type === "internal"
                  ? 1
                  : null,
            }
          )
        : await axios.post<MemberSearchDto>(
            `${GATEWAY_URL}/wapi/workgroups/members/searchAll/paging`,
            req
          );
    return resp.data;
  }
  async getWorkgroupFullList(
    req: WorkgroupSearchDto
  ): Promise<WorkgroupRespDto> {
    const resp = await axios.post<WorkgroupRespDto>(
      `${GATEWAY_URL}/wapi/workgroups/list`,
      req
    );
    return resp.data;
  }
  async getTotalFavoriteItems(
    req: FavoriteItemListReqDto
  ): Promise<FavoriteItemListRespDto> {
    const resp = await axios.post<FavoriteItemListRespDto>(
      `${GATEWAY_URL}/wapi/workgroups/favorite/item/list/total`,
      req
    );
    return resp.data;
  }
  async getFavoriteMeetingList(
    req: FavoriteItemListReqDto
  ): Promise<ZoomRespondDto> {
    const resp = await axios.post<ZoomRespondDto>(
      `${GATEWAY_URL}/wapi/workgroups/favorite/meeting/list`,
      req
    );
    return Promise.resolve(resp.data);
  }
  async doWorkgroupFavorite(
    workgroupId: string,
    users: { userId: string; favorite: string }[]
  ): Promise<Workgroup[]> {
    const resp = await axios.post<Workgroup[]>(
      `${GATEWAY_URL}/wapi/workgroups/favorite`,
      {
        workgroupId,
        users,
      }
    );
    return Promise.resolve(resp.data);
  }
  async getWorkgroupDetail(req: {
    workgroupId: string;
  }): Promise<WorkgroupDetailDTO> {
    const resp = await axios.post<WorkgroupDetailDTO>(
      `${GATEWAY_URL}/wapi/workgroups`,
      req
    );
    return resp.data;
  }
  async membersProfile(userId: string): Promise<Member> {
    const resp = await axios.post<Member>(
      `${GATEWAY_URL}/wapi/workgroups/members`,
      {
        userId,
      }
    );
    return Promise.resolve(resp.data);
  }
  async workgroupChangeInfo(users: UserSimpleInfo[]): Promise<BasicReturnMsg> {
    const resp = await axios.post<BasicReturnMsg>(
      `${GATEWAY_URL}/wapi/workgroups/members/update`,
      { users }
    );
    return Promise.resolve(resp.data);
  }
  async getWorkspaceConfig(): Promise<EcoCommonConfig> {
    const resp = await axios.get<EcoCommonConfig>(
      `${GATEWAY_URL}/wapi/tenancy/config`
    );
    return Promise.resolve(resp.data);
  }
  async createNewWorkgroup(
    workgroupRequest: WorkgroupWithPermissionPolicyTarget
  ): Promise<Workgroup> {
    const resp = await axios.post<Workgroup>(
      `${GATEWAY_URL}/wapi/workgroups/insert`,
      workgroupRequest
    );
    return Promise.resolve(resp.data);
  }
  async createNewPrivateWorkgroups(
    workgroupRequest: WorkgroupWithPermissionPolicyTarget
  ): Promise<Workgroup> {
    const resp = await axiosNoLoading.post<Workgroup>(
      `${GATEWAY_URL}/wapi/workgroups/insert/private`,
      workgroupRequest
    );
    return Promise.resolve(resp.data);
  }
  async updateWorkgroupInfo(req: {
    workgroupId: string;
    managerId?: string;
    workgroup?: Workgroup;
    policy?: Policy;
  }): Promise<BasicReturnMsg> {
    const resp = await axios.post<BasicReturnMsg>(
      `${GATEWAY_URL}/wapi/workgroups/update`,
      req.managerId
        ? {
            workgroup: {
              managerId: req.managerId,
              workgroupId: req.workgroupId,
            },
          }
        : req
    );
    return Promise.resolve(resp.data);
  }
  async resendInviteMail(workgroupId: string, userId: string): Promise<any> {
    const resp = await axios.get<any>(
      `${GATEWAY_URL}/wapi/workgroups/members/resend/${workgroupId}/${userId}`
    );
    return Promise.resolve(resp.data);
  }
  async getWorkgroupNoti(workgroupId: string): Promise<NotificationDTO> {
    const resp = await axios.get<NotificationDTO>(
      `${GATEWAY_URL}/wapi/policy/notification/${workgroupId}`
    );
    return Promise.resolve(resp.data);
  }
  async updateWorkgroupNoti(
    workgroupId: string,
    req: NotificationDTO
  ): Promise<NotificationDTO> {
    const resp = await axios.post<NotificationDTO>(
      `${GATEWAY_URL}/wapi/policy/notification/${workgroupId}`,
      {
        workgroupId: workgroupId,
        notificationPolicy: req,
      }
    );
    return Promise.resolve(resp.data);
  } /* 워크 그룹 삭제 */
  async workgroupUpdateStatus(
    workgroupId: string,
    status: number
  ): Promise<Workgroup[]> {
    const resp = await axios.post<Workgroup[]>(
      `${GATEWAY_URL}/wapi/workgroups/update/status`,
      {
        workgroupId,
        status,
      }
    );
    return Promise.resolve(resp.data);
  } /* 워크그룹 내 멤버 자진 탈퇴 */
  async workgroupmemberWithdraw(
    workgroupId: string,
    users: {
      userId: string;
      userType: number;
      withdrawYN: string;
    }[]
  ): Promise<Member> {
    const resp = await axios.post<Member>(
      `${GATEWAY_URL}/wapi/workgroups/members/withdraw`,
      {
        workgroupId,
        users,
      }
    );

    return Promise.resolve(resp.data);
  }
  async workgroupHomeDashboard(
    workgroupId: string
  ): Promise<WorkgroupHomeDashboard> {
    const resp = await axios.get<WorkgroupHomeDashboard>(
      `${GATEWAY_URL}/wapi/workgroups/home/dashboard/${workgroupId}`
    );
    return Promise.resolve(resp.data);
  }
  async getMyDashboardTasks(): Promise<MyTaskDashboard> {
    const resp = await axios.get<MyTaskDashboard>(
      `${GATEWAY_URL}/wapi/dashboard/tasks`
    );
    return Promise.resolve(resp.data);
  }
  async getMyDashboardSuggestion(): Promise<MyTaskDashboard> {
    const resp = await axios.get<MyTaskDashboard>(
      `${GATEWAY_URL}/wapi/dashboard/suggestions`
    );
    return Promise.resolve(resp.data);
  }
  async getWorkgroupCalendarCount(req: {
    workgroupId: string;
    date: number;
    startDate?: number;
    endDate?: number;
    offset: number;
  }): Promise<CalendarCount[]> {
    const resp = await axios.get<CalendarCount[]>(
      req.startDate
        ? `${GATEWAY_URL}/wapi/workgroups/calendar/${req.workgroupId}/count?startDate=${req.startDate}&endDate=${req.endDate}&offset=${req.offset}`
        : `${GATEWAY_URL}/wapi/workgroups/calendar/${req.workgroupId}/count?date=${req.date}&offset=${req.offset}`
    );
    return Promise.resolve(resp.data);
  }
  async getWorkgroupCalendarDetail(req: {
    workgroupId: string;
    date: number;
  }): Promise<CalendarDetail> {
    const resp = await axios.get<CalendarDetail>(
      `${GATEWAY_URL}/wapi/workgroups/calendar/${req.workgroupId}/detail?date=${req.date}`
    );
    return Promise.resolve(resp.data);
  }
  // 홈 - 최근 이력이 있는 워크그룹 리스트 호출
  async getRecentHistoryWorkgroupList(req: {
    startDate: number;
    endDate: number;
  }): Promise<Workgroup[]> {
    const resp = await axios.get<Workgroup[]>(
      `${GATEWAY_URL}/wapi/workgroups/recent/list?startDate=${req.startDate}&endDate=${req.endDate}`
    );
    return Promise.resolve(resp.data);
  }

  async deleteMember(userId: string): Promise<BasicReturnMsg> {
    const resp = await axios.post<BasicReturnMsg>(
      `${GATEWAY_URL}/wapi/workgroups/members/delete/user`,
      {
        userId,
      }
    );

    return Promise.resolve(resp.data);
  }
  async checkCheckoutUser(req: {
    userIds: string[];
    workgroupId?: string;
  }): Promise<BasicReturnMsg> {
    const resp = req.workgroupId
      ? await axios.post<BasicReturnMsg>(
          `${GATEWAY_URL}/wapi/workgroups/members/checkout`,
          req
        )
      : await axiosAdmin.post<BasicReturnMsg>(
          `${GATEWAY_URL}/wapi/workgroups/members/checkout`,
          req
        );

    return Promise.resolve(resp.data);
  }
  async getNesetedWorkgroupList(): Promise<Workgroup[]> {
    const resp = await axios.get<Workgroup[]>(
      `${GATEWAY_URL}/wapi/workgroups/tree`
    );
    return Promise.resolve(resp.data);
  }
  async moveWorkgroup(req: {
    workgroupId: string;
    targetParentWorkgroupId: string;
  }): Promise<Workgroup> {
    const resp = await axios.post<Workgroup>(
      `${GATEWAY_URL}/wapi/workgroups/update/tree`,
      req
    );
    return Promise.resolve(resp.data);
  }
}
const workgroupApi: WorkgroupApi = new WorkgroupServerApi();
export default workgroupApi;
